import { Stack, router, type Href } from 'expo-router';
import { BellIcon, ChevronRightIcon, LinkIcon, ShieldIcon, UserCogIcon } from 'lucide-react-native';
import * as React from 'react';
import { Pressable, ScrollView, View } from 'react-native';

import { Icon } from '@sixam/ui/icon';
import { Text } from '@sixam/ui/text';
import { useReleaseFeatureFlag } from '@/lib/use-release-feature-flag';

type SettingsRow = Readonly<{
  description: string;
  href: Href;
  icon: typeof UserCogIcon;
  label: string;
}>;

/**
 * Member settings only — everything here is about this account, not a club.
 * Club configuration lives under the admin area for the clubs you manage.
 */
const ACCOUNT_SETTINGS: SettingsRow = {
  description: 'Email, phone, and account actions',
  href: '/app/settings/account' as Href,
  icon: UserCogIcon,
  label: 'Account',
};

const NOTIFICATION_SETTINGS: SettingsRow = {
  description: 'Reminders you set for upcoming runs',
  href: '/app/notifications' as Href,
  icon: BellIcon,
  label: 'Notifications',
};

const INTEGRATION_SETTINGS: SettingsRow = {
  description: 'Strava, Instagram, and calendar connections',
  href: '/app/settings/integrations' as Href,
  icon: LinkIcon,
  label: 'Connections',
};

// Never gated: reporting and blocking have to be reachable in every release.
const SAFETY_SETTINGS: SettingsRow = {
  description: 'Community terms, reporting, and blocked members',
  href: '/app/settings/safety' as Href,
  icon: ShieldIcon,
  label: 'Safety',
};

export default function MemberSettingsScreen() {
  const eventRemindersEnabled = useReleaseFeatureFlag('eventReminders') === true;
  const integrationSettingsEnabled = useReleaseFeatureFlag('integrationSettings') === true;
  const settings = [
    ACCOUNT_SETTINGS,
    SAFETY_SETTINGS,
    ...(eventRemindersEnabled ? [NOTIFICATION_SETTINGS] : []),
    ...(integrationSettingsEnabled ? [INTEGRATION_SETTINGS] : []),
  ];

  return (
    <>
      <Stack.Screen options={{ headerShown: true, title: 'Settings' }} />
      <ScrollView
        className="flex-1 bg-background"
        contentContainerClassName="gap-4 p-6"
        contentInsetAdjustmentBehavior="automatic"
      >
        <View className="gap-1">
          <Text variant="large">Your account</Text>
          <Text variant="muted">Settings for you as a member, not for a club.</Text>
        </View>

        <View className="gap-2">
          {settings.map((row) => (
            <Pressable
              accessibilityRole="button"
              className="flex-row items-center gap-3 rounded-md border border-border p-4 active:opacity-70"
              key={row.label}
              onPress={() => router.push(row.href)}
            >
              <Icon as={row.icon} className="size-5 text-foreground" />
              <View className="min-w-0 flex-1">
                <Text className="font-bold">{row.label}</Text>
                <Text variant="muted">{row.description}</Text>
              </View>
              <Icon as={ChevronRightIcon} className="size-5 text-muted-foreground" />
            </Pressable>
          ))}
        </View>
      </ScrollView>
    </>
  );
}
